import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type { CynetAlert, CynetCiLink, CynetHost, CynetVulnerability } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { Card, EmptyState, ErrorBanner, PageSection, SectionedPage, Spinner, StatCard, formatDateTime } from '../components/common'
import { Connessioni } from '../components/Connessioni'
import { DataTable } from '../components/DataTable'
import { NotaCache } from '../components/NotaCache'
import { useVivo } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

/** Severity colour of an alert or a vulnerability: red→amber→blue→grey, never green. */
const COLORE_GRAVITA: Record<string, string> = {
  critical: 'var(--error)',
  high: 'var(--warn)',
  medium: 'var(--info)',
  low: 'var(--text-muted)',
  info: 'var(--text-muted)',
}
const ORDINE_GRAVITA: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 }

function etichettaGravita(g: string): string {
  switch (g) {
    case 'critical': return t('critica')
    case 'high': return t('alta')
    case 'medium': return t('media')
    case 'low': return t('bassa')
    case 'info': return t('informativa')
    default: return g || '—'
  }
}

function etichettaStato(s: string): string {
  switch (s) {
    case 'open': return t('aperto')
    case 'in_progress': return t('in lavorazione')
    case 'closed': return t('chiuso')
    case 'ignored': return t('ignorato')
    default: return s || '—'
  }
}

const vero = (v?: string) => ['true', '1', 'online', 'connected', 'yes', 'isolated'].includes((v ?? '').trim().toLowerCase())

function Gravita({ g }: { g: string }) {
  return <span style={{ color: COLORE_GRAVITA[g] ?? 'var(--text-muted)', fontWeight: 600 }}>{etichettaGravita(g)}</span>
}

/** The CI a Cynet record is about, or the name Cynet gives when CENSO does not have it. */
function HostCi({ ci, nome, ip }: { ci?: CynetCiLink | null; nome: string; ip?: string }) {
  if (ci) return <Link to={`/cis/${ci.id}`}>{ci.name}</Link>
  return <span>{nome || ip || '—'}{nome && <span className="muted"> · {t('non in CMDB')}</span>}</span>
}

/**
 * The Cynet page: the EDR/XDR source seen from CENSO. It does not repeat the
 * Cynet console — it shows what CENSO can do and Cynet cannot: the
 * **coverage** (which inventory assets have no agent), and links every alert,
 * vulnerable host and endpoint to its CI. Sections: overview and coverage,
 * the recent alerts (type, user, file), the vulnerabilities grouped by CVE with
 * the affected hosts, every Cynet host with what Cynet says of it (group,
 * domain, last user, isolation). The page opens from the copy of the last read
 * (`useVivo` + `NotaCache`); coverage and CI links are computed against today's
 * CMDB at every reply. The periodic sync brings hosts and findings into the
 * CMDB; admins have «Esegui adesso».
 */
export default function Cynet() {
  const { principal } = useAuth()
  const admin = Boolean(principal?.roles.includes('tenant_admin') || principal?.roles.includes('provider_admin'))
  // Both from the same copy of the last Cynet read; «Aggiorna» reads again in
  // the background (the second request finds the refresh already running).
  const overview = useVivo((refresh) => api.cynetOverview(refresh), [])
  const coverage = useVivo((refresh) => api.cynetCoverage(refresh), [])
  const o = overview.data
  const c = coverage.data

  const [busy, setBusy] = useState(false)
  const [esito, setEsito] = useState<string>('')
  const [errore, setErrore] = useState<unknown>(null)

  async function run() {
    setBusy(true)
    setEsito('')
    setErrore(null)
    try {
      const r = await api.runCynet()
      setEsito(t('Sincronizzati {h} host, {f} reperti scritti, {c} chiusi.', { h: r.hosts, f: r.findings, c: r.closed }))
      // The sync rewrote the page copy with what it read: just ask for it.
      void overview.rileggiCopia()
      void coverage.rileggiCopia()
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(false)
    }
  }

  const cov = c?.totals ?? o?.coverage
  const allarmi = o?.alerts?.items ?? []
  const aperti = allarmi.filter((a) => a.status_label === 'open' || a.status_label === 'in_progress').length
  const cve = o?.vulnerabilities?.items ?? []

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">Cynet</h1>
          <p className="page__subtitle">
            {t('La copertura dell’EDR Cynet sul parco: quali asset sono protetti, quali no, più allarmi e vulnerabilità sui CI.')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="secondary" onClick={() => { void overview.reload(); void coverage.reload() }} disabled={overview.loading}>
            {overview.loading ? t('Interrogo…') : t('Aggiorna')}
          </button>
          {admin && (
            <button onClick={() => void run()} disabled={busy || !o?.available}>
              {busy ? t('Sincronizzo…') : t('Esegui adesso')}
            </button>
          )}
        </div>
      </header>

      <ErrorBanner error={overview.error || errore} />
      <NotaCache dati={o} />
      {esito && <p className="field__hint" style={{ color: 'var(--ok)' }}>{esito}</p>}

      {overview.loading && !o ? (
        <Spinner />
      ) : !o ? null : !o.available ? (
        <>
          <EmptyState title={t('Cynet non è interrogabile.')} hint={o.reason ?? ''} />
          <h2 className="sezione-pagina__titolo" id="connessioni">{t('Connessioni')}</h2>
          <Connessioni funzioni={['cynet']} segnaposto="cynet" onChanged={() => { void overview.reload(); void coverage.reload() }} />
        </>
      ) : (
        <SectionedPage
          sections={[
            { id: 'panoramica', label: t('Panoramica') },
            { id: 'allarmi', label: t('Allarmi ({n})', { n: o.alerts?.total ?? 0 }) },
            { id: 'vulnerabilita', label: t('Vulnerabilità ({n})', { n: cve.length }) },
            { id: 'host', label: t('Host ({n})', { n: o.hosts?.total ?? 0 }) },
            { id: 'connessioni', label: t('Connessioni') },
          ]}
        >
          <PageSection id="panoramica">
            <div className="wazuh-kpi">
              <StatCard label={t('Host con l’agente')} value={o.hosts?.total ?? 0} />
              <StatCard label={t('online')} value={o.hosts?.online ?? 0} tone="ok" />
              {(o.hosts?.isolated ?? 0) > 0 && <StatCard label={t('isolati')} value={o.hosts?.isolated ?? 0} tone="warn" />}
              <StatCard label={t('Allarmi aperti')} value={aperti} tone={aperti ? 'warn' : 'ok'} />
              <StatCard label={t('CVE')} value={cve.length} hint={t('{n} host×CVE', { n: o.vulnerabilities?.total ?? 0 })} />
            </div>

            <Card title={t('Copertura EDR')}>
              <p className="field__hint" style={{ marginTop: 0 }}>
                {t('Degli asset di CENSO che possono portare un agente (PC, server), quanti Cynet li protegge. Gli apparecchi che non possono averlo (medicali, rete, stampanti) sono «non applicabili».')}
              </p>
              {coverage.loading && !c ? <Spinner /> : (
                <div className="wazuh-kpi">
                  <StatCard label={t('Protetti')} value={cov?.protected ?? 0} tone="ok" />
                  <StatCard label={t('Non protetti')} value={cov?.unprotected ?? 0} tone={(cov?.unprotected ?? 0) > 0 ? 'error' : 'ok'} />
                  <StatCard label={t('Non applicabili')} value={cov?.not_applicable ?? 0} />
                  <StatCard label={t('Solo in Cynet')} value={cov?.only_in_cynet ?? 0} tone={(cov?.only_in_cynet ?? 0) > 0 ? 'warn' : 'ok'} />
                </div>
              )}
            </Card>

            {(c?.unprotected ?? []).length > 0 && (
              <Card title={t('Asset non protetti dall’EDR')}>
                <p className="field__hint" style={{ marginTop: 0 }}>
                  {t('Endpoint che CENSO conosce ma su cui Cynet non vede l’agente: da coprire o da verificare.')}
                </p>
                <DataTable
                  rows={c?.unprotected ?? []}
                  rowKey={(r) => r.ci_id}
                  initialSort={{ key: 'name' }}
                  conteggio={{ nome: t('asset') }}
                  paginazione={{ predefinita: 50 }}
                  columns={[
                    { key: 'name', header: t('Nome'), value: (r) => r.name, render: (r) => <Link to={`/cis/${r.ci_id}`}>{r.name}</Link> },
                    { key: 'ci_class', header: t('Classe'), value: (r) => t(r.ci_class || '—') },
                    { key: 'os', header: t('Sistema operativo'), value: (r) => r.os || '—' },
                  ]}
                />
              </Card>
            )}

            {(c?.only_in_cynet ?? []).length > 0 && (
              <Card title={t('Host solo in Cynet')}>
                <p className="field__hint" style={{ marginTop: 0 }}>
                  {t('Host che Cynet protegge ma CENSO non ha ancora in inventario: la sincronizzazione li aggiunge.')}
                </p>
                <DataTable
                  rows={c?.only_in_cynet ?? []}
                  rowKey={(h, i) => `${h.hostname}-${i}`}
                  initialSort={{ key: 'hostname' }}
                  paginazione={{ predefinita: 50 }}
                  columns={[
                    { key: 'hostname', header: t('Nome'), value: (h) => h.hostname || '—' },
                    { key: 'ip', header: t('Indirizzo IP'), mono: true, value: (h) => h.ip || '—' },
                    { key: 'os', header: t('Sistema operativo'), value: (h) => h.os || '—' },
                  ]}
                />
              </Card>
            )}

            <p className="field__hint">
              {t('Interrogato')}: {formatDateTime(o.queried_at)}{o.server?.name ? ` · ${o.server.name}` : ''}
            </p>
          </PageSection>

          <PageSection id="allarmi">
            <Allarmi allarmi={allarmi} totale={o.alerts?.total ?? 0} perGravita={o.alerts?.by_severity ?? {}} />
          </PageSection>

          <PageSection id="vulnerabilita">
            <Vulnerabilita cve={cve} />
          </PageSection>

          <PageSection id="host">
            <HostCynet host={c?.hosts ?? []} caricamento={coverage.loading && !c} />
          </PageSection>

          <PageSection id="connessioni">
            <Connessioni funzioni={['cynet']} segnaposto="cynet" onChanged={() => { void overview.reload(); void coverage.reload() }} />
          </PageSection>
        </SectionedPage>
      )}
    </>
  )
}

/** The recent alerts: open ones by default, with type, user and file when Cynet gives them. */
function Allarmi({ allarmi, totale, perGravita }: { allarmi: CynetAlert[]; totale: number; perGravita: Record<string, number> }) {
  const [soloAperti, setSoloAperti] = useState(true)
  const righe = soloAperti ? allarmi.filter((a) => a.status_label === 'open' || a.status_label === 'in_progress') : allarmi
  return (
    <Card title={t('Allarmi')}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Gli allarmi di Cynet degli ultimi 30 giorni (i più recenti, fino a 500 di {n}); quelli aperti diventano reperti sul CI.', { n: totale })}
      </p>
      {Object.keys(perGravita).length > 0 && (
        <p className="dns-chips" style={{ margin: '0 0 10px' }}>
          {Object.entries(perGravita).sort((a, b) => (ORDINE_GRAVITA[a[0]] ?? 9) - (ORDINE_GRAVITA[b[0]] ?? 9)).map(([g, n]) => (
            <span key={g} className="dns-chip" style={{ cursor: 'default' }}><Gravita g={g} /> <span className="dns-chip__n">{n}</span></span>
          ))}
        </p>
      )}
      <label className="checkbox" style={{ marginBottom: 10 }}>
        <input type="checkbox" checked={soloAperti} onChange={(e) => setSoloAperti(e.target.checked)} />{' '}
        {t('Solo aperti o in lavorazione')}
      </label>
      <DataTable
        rows={righe}
        rowKey={(a, i) => `${a.id}-${i}`}
        initialSort={{ key: 'time', dir: 'desc' }}
        conteggio={{ nome: t('allarmi') }}
        paginazione={{ predefinita: 50 }}
        emptyText={soloAperti ? t('Nessun allarme aperto.') : t('Nessun allarme.')}
        columns={[
          { key: 'time', header: t('Quando'), value: (a) => a.time, render: (a) => (a.time ? formatDateTime(a.time) : '—') },
          { key: 'severity', header: t('Gravità'), value: (a) => ORDINE_GRAVITA[a.severity] ?? 9, render: (a) => <Gravita g={a.severity} /> },
          {
            key: 'name', header: t('Allarme'), value: (a) => `${a.name} ${a.type ?? ''}`,
            render: (a) => (
              <span title={a.description || undefined}>
                {a.name || '—'}{a.type && <span className="muted"> · {a.type}</span>}
              </span>
            ),
          },
          { key: 'host', header: t('Host'), value: (a) => a.ci?.name ?? a.hostname, render: (a) => <HostCi ci={a.ci} nome={a.hostname} ip={a.ip} /> },
          { key: 'user', header: t('Utente'), value: (a) => a.user ?? '' },
          { key: 'file', header: t('File'), mono: true, value: (a) => a.file ?? '' },
          { key: 'status', header: t('Stato'), value: (a) => etichettaStato(a.status_label) },
        ]}
      />
    </Card>
  )
}

/** The vulnerabilities grouped by CVE, the most severe first; a row opens the affected hosts. */
function Vulnerabilita({ cve }: { cve: CynetVulnerability[] }) {
  return (
    <Card title={t('Vulnerabilità')}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Le vulnerabilità che Cynet trova sugli endpoint (modulo ESPM), raggruppate per CVE, le più gravi prima: apri una riga per vedere gli host colpiti. Diventano reperti sui CI.')}
      </p>
      <DataTable
        rows={cve}
        rowKey={(v) => v.cve}
        initialSort={{ key: 'severity' }}
        conteggio={{ nome: t('CVE') }}
        paginazione={{ predefinita: 50 }}
        emptyText={t('Nessuna vulnerabilità aperta (o il Site GUID non è configurato: senza, Cynet non dà le vulnerabilità).')}
        expand={(v) => (
          <ul className="lista-semplice" style={{ margin: 0 }}>
            {v.hosts.map((h, i) => (
              <li key={`${h.hostname}-${i}`}><HostCi ci={h.ci} nome={h.hostname} ip={h.ip} /></li>
            ))}
            {v.host_count > v.hosts.length && <li className="muted">{t('e altri {n}', { n: v.host_count - v.hosts.length })}</li>}
          </ul>
        )}
        columns={[
          {
            key: 'cve', header: 'CVE', mono: true, value: (v) => v.cve,
            render: (v) => (v.nist ? <a href={v.nist} target="_blank" rel="noreferrer">{v.cve}</a> : v.cve),
          },
          { key: 'product', header: t('Prodotto'), value: (v) => v.product, render: (v) => <span title={v.description || undefined}>{v.product || '—'}</span> },
          { key: 'severity', header: t('Gravità'), value: (v) => ORDINE_GRAVITA[v.severity] ?? 9, render: (v) => <Gravita g={v.severity} /> },
          { key: 'score', header: t('Punteggio'), value: (v) => v.score ?? 0, render: (v) => (v.score == null ? '—' : String(v.score)) },
          { key: 'host_count', header: t('Host'), value: (v) => v.host_count },
        ]}
      />
    </Card>
  )
}

/** Every host Cynet protects, with what Cynet says of it and its CI. */
function HostCynet({ host, caricamento }: { host: CynetHost[]; caricamento: boolean }) {
  if (caricamento) return <Spinner />
  const colonne = [
    { key: 'hostname', header: t('Host'), value: (h: CynetHost) => h.ci?.name ?? h.hostname, render: (h: CynetHost) => <HostCi ci={h.ci} nome={h.hostname} ip={h.ip} /> },
    { key: 'ip', header: t('Indirizzo IP'), mono: true, value: (h: CynetHost) => h.ip || '—' },
    { key: 'os', header: t('Sistema operativo'), value: (h: CynetHost) => h.os || h.platform || '—' },
    { key: 'agent_version', header: t('Agente'), value: (h: CynetHost) => h.agent_version || '—' },
    {
      key: 'online', header: t('Stato'), value: (h: CynetHost) => (vero(h.online) ? 1 : 0),
      render: (h: CynetHost) => (
        <>
          {h.online ? (vero(h.online) ? <span className="badge badge--ok">{t('online')}</span> : <span className="badge badge--muted">{t('offline')}</span>) : '—'}
          {vero(h.isolated) && <> <span className="badge badge--warn">{t('isolato')}</span></>}
        </>
      ),
    },
    { key: 'last_seen', header: t('Ultima scansione'), value: (h: CynetHost) => h.last_seen, render: (h: CynetHost) => (h.last_seen ? formatDateTime(h.last_seen) : '—') },
    { key: 'group', header: t('Gruppo'), value: (h: CynetHost) => h.group ?? '' },
    { key: 'domain', header: t('Dominio'), value: (h: CynetHost) => h.domain ?? '' },
    { key: 'last_user', header: t('Ultimo utente'), value: (h: CynetHost) => h.last_user ?? '' },
  ]
  return (
    <Card title={t('Host in Cynet')}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Gli endpoint con l’agente Cynet, con quello che Cynet sa di loro e il CI corrispondente in CENSO. Gruppo, dominio e ultimo utente compaiono se la console li fornisce.')}
      </p>
      <DataTable
        rows={host}
        rowKey={(h, i) => `${h.hostname}-${i}`}
        initialSort={{ key: 'hostname' }}
        conteggio={{ nome: t('host') }}
        paginazione={{ predefinita: 50 }}
        emptyText={t('Nessun host.')}
        columns={colonne}
      />
    </Card>
  )
}
